// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: WKWebView and Android WebView have no Notification API, but the whole
// notification settings / nagbar flow is written against it. Inside the native
// shell we install a small stand-in whose permission mirrors the OS permission
// held by KonvoPush. Pushes themselves are shown by the OS, so constructing a
// Notification here shows nothing: the app is in the foreground whenever this
// code runs, and native suppresses banners for the open chat.

import {
	isNativeMobileShell,
	type NativePermissionState,
	NativePlugins,
} from '@app/features/platform/native_mobile/NativeMobileBridge';

type ShimPermission = 'default' | 'granted' | 'denied';

let permission: ShimPermission = 'default';
const permissionListeners = new Set<(value: ShimPermission) => void>();

function toShimPermission(state: NativePermissionState): ShimPermission {
	if (state === 'granted' || state === 'provisional') return 'granted';
	if (state === 'denied') return 'denied';
	return 'default';
}

function setPermission(next: ShimPermission): void {
	if (next === permission) return;
	permission = next;
	for (const listener of permissionListeners) listener(next);
}

export function onNativeNotificationPermissionChange(listener: (value: ShimPermission) => void): () => void {
	permissionListeners.add(listener);
	return () => permissionListeners.delete(listener);
}

export async function refreshNativeNotificationPermission(): Promise<ShimPermission> {
	const push = NativePlugins.push();
	if (!push) return permission;
	try {
		const {state} = await push.getPermission();
		setPermission(toShimPermission(state));
	} catch {}
	return permission;
}

class NativeNotificationStandIn extends EventTarget {
	static get permission(): ShimPermission {
		return permission;
	}

	static async requestPermission(callback?: (value: ShimPermission) => void): Promise<ShimPermission> {
		const push = NativePlugins.push();
		if (push) {
			try {
				const {state} = await push.requestPermission();
				setPermission(toShimPermission(state));
			} catch {}
		}
		callback?.(permission);
		return permission;
	}

	readonly title: string;
	readonly body: string;
	readonly tag: string;
	readonly data: unknown;
	onclick: ((event: Event) => void) | null = null;
	onclose: ((event: Event) => void) | null = null;
	onerror: ((event: Event) => void) | null = null;
	onshow: ((event: Event) => void) | null = null;

	constructor(title: string, options: NotificationOptions = {}) {
		super();
		this.title = title;
		this.body = options.body ?? '';
		this.tag = options.tag ?? '';
		this.data = options.data ?? null;
	}

	close(): void {}
}

export function installNativeNotificationShim(): void {
	if (!isNativeMobileShell()) return;
	if (typeof (window as {Notification?: unknown}).Notification !== 'undefined') return;
	Object.defineProperty(window, 'Notification', {
		configurable: true,
		writable: true,
		value: NativeNotificationStandIn,
	});
	void refreshNativeNotificationPermission();
}
